import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';

export interface SelectConfig {
  label: string;
  value: string | number;
  optionDetails?: OptionDetails[];
  clearable?: boolean;
  searchable?: boolean;
  titleProp?: string;
  groupBy?: string;
  addTag?: boolean;
  fnSearch?: (termo: string, entidade: any) => boolean;
  prefixIcon?: {
    class: string;
    title: string;
    show: (item: any) => boolean
  }
}

interface OptionDetails {
  prop: string;
  pipe?: string;
}

@Component({
  selector: 'form-control',
  templateUrl: './form-control.component.html',
  styleUrls: ['./form-control.component.scss']
})
export class FormControlComponent implements AfterViewInit, OnChanges {

  constructor(private fb: FormBuilder) { }

  cor: string = localStorage.getItem('cor') || 'blue';

  @Input() label: string = null;
  @Input() id: string = '';
  @Input() form: FormGroup = this.fb.group({});
  @Input() control: string = '';
  @Input() type: string = '';
  @Input() placeholder: string = null;
  @Input() maxLength: number = null;
  @Input() minLength: number = null;
  @Input() max: number | string = null;
  @Input() min: number | string = null;
  @Input() readonly: boolean = null;
  @Input() disabled: boolean = null;
  @Input() required: boolean = null;

  @Input() mascara: string = '';
  @Input() showMascara: boolean = false;
  @Input() maskCustomPatterns: RegExp = null;

  @Input() accept: string = '*';

  @Input() loading: boolean = false;
  @Input() multiple: boolean = false;
  @Input() selectOptions: any[] = null;
  @Input() selectConfig: SelectConfig = null;

  @Input() dateMode: string = 'single';

  @Input('myModel') modelInput: any;
  @Output('myModelChange') onChange: EventEmitter<any> = new EventEmitter();

  @Output() onSelectScrollToEnd: EventEmitter<null> = new EventEmitter();
  @Output() onSelectSearch: EventEmitter<string> = new EventEmitter();
  @Output() onSelectClear: EventEmitter<string> = new EventEmitter();

  showPassword: boolean = false;

  ngAfterViewInit(): void {
    this.updateForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isForm && changes.form) {
      this.form.patchValue(changes.form.currentValue.value);
      this.modelInput = this.form.get(this.control).value;

      this.updateForm();
    }
  }

  get isForm() {
    return this.control !== '';
  }

  private updateForm() {
    if (this.isForm) {
      const validators = [];

      this.required = this.hasValidator('required');

      if (this.required)
        validators.push(Validators.required);

      if (this.type === 'email')
        validators.push(Validators.email);

      if (this.maxLength)
        validators.push(Validators.maxLength(this.maxLength));

      if (this.minLength)
        validators.push(Validators.minLength(this.minLength));

      if (this.max)
        validators.push(Validators.max(Number(this.max)));

      if (this.min)
        validators.push(Validators.min(Number(this.min)));

      validators.forEach(validator => {
        this.form.get(this.control).setValidators([
          validator,
          this.form.get(this.control).validator
        ]);
      });

      this.disabled = this.form.get(this.control).disabled;

      if (this.disabled)
        this.form.get(this.control).disable();
    }

  }

  private hasValidator(validatorStr: string) {
    const control = this.form.get(this.control);

    if (!control.validator)
      return false;

    const validator = control.validator({} as AbstractControl);
    return validator && validator[validatorStr];
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  reset() {
    if (this.isForm) {
      this.form.get(this.control).reset();
      this.form.get(this.control).markAsTouched();
      this.modelInput = this.form.get(this.control).value;
    }
  }

  focus() {
    const inputs = document.getElementsByTagName('input');

    Object.entries(inputs).forEach(([string, input]) => {
      if (input.id === this.id)
        input.focus();
    });
  }

  emitChangeEvent() {
    const valor = this.isForm ? this.form.get(this.control).value : this.modelInput;

    this.onChange.emit(valor);
  }

  clearSearch() {
    if (this.isForm)
      this.form.get(this.control).setValue(null);
    else
      this.modelInput = null;

    this.onChange.emit(null);
  }
}
